import type { TaskListItem as TaskListItemType } from '@/types/task';
import Badge from '@/components/common/Badge';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { useSets } from '@/hooks/useSets';

interface TaskListItemProps {
  task: TaskListItemType;
  onClick: () => void;
  /** Whether to show the Set name badge (useful when viewing "All Tasks") */
  showSetName?: boolean;
}

export default function TaskListItem({ task, onClick, showSetName = false }: TaskListItemProps) {
  const { data: setsData } = useSets('active');

  // Resolve set name from the sets data
  const getSetName = (): string | null => {
    if (!showSetName) return null;
    if (!task.set_id) return 'General';
    const set = setsData?.sets.find((s) => s.set_id === task.set_id);
    return set?.name ?? 'General';
  };

  const setName = getSetName();

  return (
    <button
      onClick={onClick}
      className="w-full min-w-0 border-b border-neutral-300 px-4 py-3 text-left transition-colors hover:bg-neutral-300"
    >
      <h3 className="text-sm font-medium text-neutral-900">{task.title}</h3>
      <div className="mt-1.5 flex h-5 flex-wrap items-center gap-2 overflow-hidden">
        {setName && (
          <Badge variant="primary">{setName}</Badge>
        )}
        {task.tags.slice(0, 3).map((tag) => (
          <Badge key={tag} variant="default">
            {tag}
          </Badge>
        ))}
        {task.tags.length > 3 && (
          <span className="text-xs text-neutral-500">+{task.tags.length - 3}</span>
        )}
      </div>
      <p className="mt-1 text-xs text-neutral-500">
        {formatRelativeTime(task.created_at)}
      </p>
    </button>
  );
}
